'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import './styles.css';

import { extractComponentFromChunk } from '@/ui/utils/tiles';
import { twJoin } from 'tailwind-merge';
import Link from 'next/link';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import { buildTrackingId } from '@/utils/tracking-helper';
import OptimizedImage from '../../shared/OptimizedImage';
import Container from '../../shared/Container';
import { RichTextRenderer } from '../../tileRenderers/RichTextRenderer';
import Button from '../../shared/Button';
import { ThreeColumnSliderV2Tile } from './types/ThreeColumnSliderV2Tile';

export default function ThreeColumnSliderV2({
  tile,
  trackingPrefix,
}: Readonly<{
  tile: ThreeColumnSliderV2Tile;
  trackingPrefix?: string;
}>) {
  const [sliderHeight, setSliderHeight] = useState(0);
  const slideRef = useRef<HTMLDivElement | null>(null);
  const height = tile.height?.options?.[0]?.value;
  const { localizedLink } = useLocaleLink();

  useEffect(() => {
    if (slideRef.current?.clientWidth) {
      const width = slideRef.current.clientWidth;
      // adding 32px because of the bottom padding
      switch (height) {
        case 'xs':
          // 4:3
          setSliderHeight(width * (3 / 4) + 32);
          break;
        case 'sm':
          // 16:9
          setSliderHeight(width * (9 / 16) + 32);
          break;
        case 'md':
          // 1:1
          setSliderHeight(width + 32);
          break;
        case 'lg':
          // 3:4
          setSliderHeight(width * (4 / 3) + 32);
          break;
        case 'xl':
          // 9:16
          setSliderHeight(width * (16 / 9) + 32);
          break;
        default:
          // 3:4
          setSliderHeight(width * (4 / 3) + 32);
          break;
      }
    }
  }, []);

  return (
    <Container>
      <Swiper
        spaceBetween={16}
        slidesPerView={1.4}
        breakpoints={{
          640: {
            slidesPerView: 2.4,
            spaceBetween: 20,
          },
          1240: {
            slidesPerView: tile.cards.chunks.length > 3 ? 3.2 : 3,
            spaceBetween: 32,
          },
        }}
        modules={[Pagination, Navigation]}
        navigation
        pagination={{
          clickable: true,
        }}
        className="three-column-slider-v2 w-full"
        style={{ height: sliderHeight }}
      >
        {tile.cards.chunks.map((card, i) => {
          const title = extractComponentFromChunk(card, 'title', 'RichTextContent');
          const subtitle = extractComponentFromChunk(card, 'subtitle', 'RichTextContent');
          const textColor = extractComponentFromChunk(card, 'text-color', 'SelectionContent')
            ?.options?.[0]?.value;
          const textPosition = extractComponentFromChunk(card, 'text-position', 'SelectionContent')
            ?.options?.[0]?.value;
          const showGradient = extractComponentFromChunk(
            card,
            'show-gradient',
            'BooleanContent',
          )?.value;
          const image = extractComponentFromChunk(card, 'image-1', 'ImageContent');
          const bgImage = extractComponentFromChunk(card, 'image', 'ImageContent');
          const bgColor = extractComponentFromChunk(card, 'bg-color', 'SelectionContent')
            ?.options?.[0]?.value;
          const buttonText = extractComponentFromChunk(
            card,
            'button-text',
            'SingleLineContent',
          )?.text;
          const link = extractComponentFromChunk(card, 'internal-link', 'ItemRelationsContent')
            ?.items?.[0]?.path;
          const linkWholeCard = extractComponentFromChunk(
            card,
            'link-whole-card',
            'BooleanContent',
          )?.value;

          let bgColorClass;
          switch (bgColor) {
            case 'primary-100':
              bgColorClass = 'bg-primary-100';
              break;
            case 'neutral-5':
              bgColorClass = 'bg-neutral-5';
              break;
            case 'neutral-10':
              bgColorClass = 'bg-neutral-10';
              break;
            case 'neutral-15':
              bgColorClass = 'bg-neutral-15';
              break;
            case 'neutral-20':
              bgColorClass = 'bg-neutral-20';
              break;
            case 'neutral-30':
              bgColorClass = 'bg-neutral-30';
              break;
            case 'neutral-40':
              bgColorClass = 'bg-neutral-40';
              break;
            case 'neutral-50':
              bgColorClass = 'bg-neutral-50';
              break;
            case 'neutral-60':
              bgColorClass = 'bg-neutral-60';
              break;
            case 'neutral-70':
              bgColorClass = 'bg-neutral-70';
              break;
            case 'neutral-80':
              bgColorClass = 'bg-neutral-80';
              break;
            case 'neutral-90':
              bgColorClass = 'bg-neutral-90';
              break;
            case 'neutral-100':
              bgColorClass = 'bg-neutral-100';
              break;
            case 'white':
              bgColorClass = 'bg-white';
              break;
            case 'primary-5':
              bgColorClass = 'bg-primary-5';
              break;
            case 'primary-10':
              bgColorClass = 'bg-primary-10';
              break;
            case 'primary-20':
              bgColorClass = 'bg-primary-20';
              break;
            case 'primary-30':
              bgColorClass = 'bg-primary-30';
              break;
            case 'primary-40':
              bgColorClass = 'bg-primary-40';
              break;
            case 'primary-50':
              bgColorClass = 'bg-primary-50';
              break;
            case 'primary-60':
              bgColorClass = 'bg-primary-60';
              break;
            case 'primary-70':
              bgColorClass = 'bg-primary-70';
              break;
            case 'primary-80':
              bgColorClass = 'bg-primary-80';
              break;
            case 'primary-90':
              bgColorClass = 'bg-primary-90';
              break;
            case 'primary-100':
              bgColorClass = 'bg-primary-100';
              break;
            default:
              bgColorClass = '';
              break;
          }

          let textPositionClasses;
          let gradientClasses;
          let imagePositionClass;
          switch (textPosition) {
            case 'top':
              textPositionClasses = 'top-5';
              gradientClasses = 'bg-gradient-to-b bottom-1/2';
              imagePositionClass = 'bottom-0';
              break;
            case 'center':
              textPositionClasses = 'top-1/2 -translate-y-1/2 text-center';
              gradientClasses = 'bg-gradient-middle top-1/2 -translate-y-1/2';
              imagePositionClass = 'top-0';
              break;
            case 'bottom':
              textPositionClasses = 'bottom-5';
              gradientClasses = 'bg-gradient-to-t top-1/2';
              imagePositionClass = 'top-0';
              break;
            default:
              textPositionClasses = 'bottom-5';
              gradientClasses = 'bg-gradient-to-t top-1/2';
              imagePositionClass = 'top-0';
              break;
          }

          let textColorClass;
          switch (textColor) {
            case 'black':
              textColorClass = 'text-black';
              break;
            case 'white':
              textColorClass = 'text-white';
              break;
            default:
              textColorClass = 'text-white';
              break;
          }

          const titleText = title?.plainText?.[0];
          const buttonTrackingId = buildTrackingId(trackingPrefix, buttonText || `slide-${i}`);
          const wholeCardTrackingId = buildTrackingId(trackingPrefix, titleText || `slide-${i}`);

          return (
            <SwiperSlide
              key={`${i}-three-col-slider-v2`}
              className={twJoin(
                'group relative z-0 overflow-hidden rounded-xl shadow-lg',
                bgColorClass,
              )}
              // Ignore TS because the ref gets passed to the SwiperSlide
              // element but it's not in the type
              // @ts-ignore
              ref={slideRef}
              // on mouse enter make shadow stronger and on mouse leave make it normal
            >
              <OptimizedImage
                imageComponent={bgImage}
                fill
                className="-z-20 object-cover"
                sizes="(max-width: 640px) 60vw, (max-width: 1240px) 40vw, 30vw"
              />
              {showGradient && (
                <div
                  className={twJoin(
                    'absolute inset-0 z-10 from-neutral-100/60 to-transparent',
                    gradientClasses,
                  )}
                />
              )}
              <div className={twJoin('absolute left-0 right-0 p-4 lg:p-6', imagePositionClass)}>
                <OptimizedImage
                  imageComponent={image}
                  width={300}
                  height={300}
                  className="h-auto w-full rounded-lg"
                  sizes="30vw"
                />
              </div>
              <div
                className={twJoin(
                  'absolute z-20 px-4 lg:px-6',
                  textPositionClasses,
                  textColorClass,
                )}
              >
                <RichTextRenderer blocks={title} className="text-2xl lg:text-3xl" />
                <RichTextRenderer blocks={subtitle} className="mb-2 text-md lg:text-2xl" />
                {!linkWholeCard && link && (
                  <Button className="w-fit" href={link} data-tile-element-id={buttonTrackingId}>
                    {buttonText}
                  </Button>
                )}
              </div>
              {linkWholeCard && link && (
                <Link
                  href={localizedLink(link)}
                  className="absolute inset-0 z-20"
                  aria-label={title?.plainText?.[0] || ''}
                  data-tile-element-id={wholeCardTrackingId}
                />
              )}
            </SwiperSlide>
          );
        })}
      </Swiper>
    </Container>
  );
}
